import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowLeft, FaPlus, FaEdit, FaTrash, FaImage } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext.tsx';
import { database } from '../../firebase/config.ts';
import { ref, onValue, push, set, remove } from 'firebase/database';
import { Product, Supplement, Category, OptionGroup } from '../../types/index.ts';
import './Products.css';

interface ProductFormData {
  name: string;
  price: string;
  description: string;
  category: string;
  image: string;
  allowSupplements: boolean;
  availableSupplements: string[];
  optionGroups: string[];
}

interface SupplementFormData {
  name: string;
  price: string;
}

interface OptionGroupFormData {
  name: string;
  type: 'single' | 'multiple';
  required: boolean;
  hasPricing: boolean;
  options: { name: string; price: string }[];
}

const Products: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [supplements, setSupplements] = useState<Supplement[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [optionGroups, setOptionGroups] = useState<OptionGroup[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showSupplementModal, setShowSupplementModal] = useState<boolean>(false);
  const [showCategoryModal, setShowCategoryModal] = useState<boolean>(false);
  const [showOptionGroupModal, setShowOptionGroupModal] = useState<boolean>(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [editingSupplement, setEditingSupplement] = useState<Supplement | null>(null);
  const [editingOptionGroup, setEditingOptionGroup] = useState<OptionGroup | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [newCategoryName, setNewCategoryName] = useState<string>('');
  const [newCategoryIcon, setNewCategoryIcon] = useState<string>('🍽️');
  const [supplementFormData, setSupplementFormData] = useState<SupplementFormData>({
    name: '',
    price: ''
  });
  const [optionGroupFormData, setOptionGroupFormData] = useState<OptionGroupFormData>({
    name: '',
    type: 'single',
    required: false,
    hasPricing: false,
    options: [{ name: '', price: '0' }]
  });
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    price: '',
    description: '',
    category: '',
    image: '',
    allowSupplements: false,
    availableSupplements: [],
    optionGroups: []
  });

  useEffect(() => {
    if (!currentUser) {
      navigate('/admin');
      return;
    }

    // Charger les catégories
    const categoriesRef = ref(database, 'categories');
    const unsubscribeCategories = onValue(categoriesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const categoriesArray = Object.entries(data).map(([id, category]: [string, any]) => ({
          id,
          ...category
        }));
        setCategories(categoriesArray);
      } else {
        // Catégories par défaut si aucune n'existe
        const defaultCategories = [
          { id: 'burgers', name: 'Burgers', icon: '🍔' },
          { id: 'pizzas', name: 'Pizzas', icon: '🍕' },
          { id: 'salades', name: 'Salades', icon: '🥗' },
          { id: 'texmex', name: 'Tex-Mex', icon: '🌮' },
          { id: 'kids', name: 'Menu Kids', icon: '👶' },
          { id: 'boissons', name: 'Boissons', icon: '🥤' }
        ];
        setCategories(defaultCategories);
      }
    });

    const productsRef = ref(database, 'products');
    const unsubscribeProducts = onValue(productsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const productsArray = Object.entries(data).map(([id, product]: [string, any]) => ({
          id,
          ...product
        }));
        setProducts(productsArray);
      } else {
        setProducts([]);
      }
    });

    const supplementsRef = ref(database, 'supplements');
    const unsubscribeSupplements = onValue(supplementsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const supplementsArray = Object.entries(data).map(([id, supplement]: [string, any]) => ({
          id,
          ...supplement
        }));
        setSupplements(supplementsArray);
      } else {
        setSupplements([]);
      }
    });

    const optionGroupsRef = ref(database, 'optionGroups');
    const unsubscribeOptionGroups = onValue(optionGroupsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const optionGroupsArray = Object.entries(data).map(([id, group]: [string, any]) => ({
          id,
          ...group
        }));
        setOptionGroups(optionGroupsArray);
      } else {
        setOptionGroups([]);
      }
    });

    return () => {
      unsubscribeCategories();
      unsubscribeProducts();
      unsubscribeSupplements();
      unsubscribeOptionGroups();
    };
  }, [currentUser, navigate]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'yolla_food');
    formData.append('cloud_name', 'djj4phbpk');

    try {
      const response = await fetch(
        'https://api.cloudinary.com/v1_1/djj4phbpk/image/upload',
        {
          method: 'POST',
          body: formData
        }
      );
      const data = await response.json();
      setFormData(prev => ({ ...prev, image: data.secure_url }));
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('Erreur lors du téléchargement de l\'image');
    }
    setUploading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const productData = {
      name: formData.name,
      price: parseFloat(formData.price),
      description: formData.description,
      category: formData.category,
      image: formData.image,
      allowSupplements: formData.allowSupplements,
      availableSupplements: formData.allowSupplements ? formData.availableSupplements : [],
      optionGroups: formData.optionGroups
    };

    try {
      if (editingProduct) {
        const productRef = ref(database, `products/${editingProduct.id}`);
        await set(productRef, productData);
      } else {
        const productsRef = ref(database, 'products');
        await push(productsRef, productData);
      }
      
      setShowModal(false);
      setEditingProduct(null);
      setFormData({
        name: '',
        price: '',
        description: '',
        category: 'burgers',
        image: '',
        allowSupplements: false,
        availableSupplements: [],
        optionGroups: []
      });
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      price: product.price.toString(),
      description: product.description,
      category: product.category,
      image: product.image,
      allowSupplements: product.allowSupplements || false,
      availableSupplements: product.availableSupplements || [],
      optionGroups: product.optionGroups || []
    });
    setShowModal(true);
  };

  const handleEditSupplement = (supplement: Supplement) => {
    setEditingSupplement(supplement);
    setSupplementFormData({
      name: supplement.name,
      price: supplement.price.toString()
    });
    setShowSupplementModal(true);
  };

  const handleEditOptionGroup = (optionGroup: OptionGroup) => {
    setEditingOptionGroup(optionGroup);
    // Déterminer si le groupe a des prix (au moins une option avec prix > 0)
    const hasPricing = optionGroup.options.some(opt => opt.price > 0);
    setOptionGroupFormData({
      name: optionGroup.name,
      type: optionGroup.type,
      required: optionGroup.required,
      hasPricing: hasPricing,
      options: optionGroup.options.map(opt => ({
        name: opt.name,
        price: opt.price.toString()
      }))
    });
    setShowOptionGroupModal(true);
  };

  const toggleSupplement = (supplementId: string) => {
    setFormData(prev => ({
      ...prev,
      availableSupplements: prev.availableSupplements.includes(supplementId)
        ? prev.availableSupplements.filter(id => id !== supplementId)
        : [...prev.availableSupplements, supplementId]
    }));
  };

  const toggleOptionGroup = (optionGroupId: string) => {
    setFormData(prev => ({
      ...prev,
      optionGroups: prev.optionGroups.includes(optionGroupId)
        ? prev.optionGroups.filter(id => id !== optionGroupId)
        : [...prev.optionGroups, optionGroupId]
    }));
  };

  const addOptionToGroup = () => {
    setOptionGroupFormData(prev => ({
      ...prev,
      options: [...prev.options, { name: '', price: '0' }]
    }));
  };

  const removeOptionFromGroup = (index: number) => {
    setOptionGroupFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const updateOptionInGroup = (index: number, field: 'name' | 'price', value: string) => {
    setOptionGroupFormData(prev => ({
      ...prev,
      options: prev.options.map((option, i) => 
        i === index ? { ...option, [field]: value } : option
      )
    }));
  };

  const handleDelete = async (productId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      try {
        const productRef = ref(database, `products/${productId}`);
        await remove(productRef);
      } catch (error) {
        console.error('Erreur:', error);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const handleDeleteSupplement = async (supplementId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce supplément ?')) {
      try {
        const supplementRef = ref(database, `supplements/${supplementId}`);
        await remove(supplementRef);
      } catch (error) {
        console.error('Erreur:', error);
        alert('Erreur lors de la suppression du supplément');
      }
    }
  };

  const handleDeleteOptionGroup = async (optionGroupId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce groupe d\'options ?')) {
      try {
        const optionGroupRef = ref(database, `optionGroups/${optionGroupId}`);
        await remove(optionGroupRef);
      } catch (error) {
        console.error('Erreur:', error);
        alert('Erreur lors de la suppression du groupe d\'options');
      }
    }
  };

  const handleSupplementSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const supplementData = {
      name: supplementFormData.name,
      price: parseFloat(supplementFormData.price)
    };

    try {
      if (editingSupplement) {
        const supplementRef = ref(database, `supplements/${editingSupplement.id}`);
        await set(supplementRef, supplementData);
      } else {
        const supplementsRef = ref(database, 'supplements');
        await push(supplementsRef, supplementData);
      }
      
      setShowSupplementModal(false);
      setEditingSupplement(null);
      setSupplementFormData({ name: '', price: '' });
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement du supplément');
    }
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Créer un ID unique basé sur le nom
    const categoryId = newCategoryName.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '');

    const categoryData = {
      name: newCategoryName,
      icon: newCategoryIcon
    };

    try {
      const categoryRef = ref(database, `categories/${categoryId}`);
      await set(categoryRef, categoryData);
      
      setShowCategoryModal(false);
      setNewCategoryName('');
      setNewCategoryIcon('🍽️');
      
      // Mettre à jour la catégorie sélectionnée dans le formulaire
      setFormData(prev => ({ ...prev, category: categoryId }));
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'ajout de la catégorie');
    }
  };

  const handleOptionGroupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Valider que toutes les options ont un nom
    const validOptions = optionGroupFormData.options.filter(opt => opt.name.trim() !== '');
    if (validOptions.length === 0) {
      alert('Veuillez ajouter au moins une option valide');
      return;
    }

    const optionGroupData = {
      name: optionGroupFormData.name,
      type: optionGroupFormData.type,
      required: optionGroupFormData.required,
      options: validOptions.map(opt => ({
        id: opt.name.toLowerCase().replace(/[^a-z0-9]/g, ''),
        name: opt.name,
        price: optionGroupFormData.hasPricing ? parseFloat(opt.price) : 0
      }))
    };

    try {
      if (editingOptionGroup) {
        const optionGroupRef = ref(database, `optionGroups/${editingOptionGroup.id}`);
        await set(optionGroupRef, optionGroupData);
      } else {
        const optionGroupsRef = ref(database, 'optionGroups');
        await push(optionGroupsRef, optionGroupData);
      }
      
      setShowOptionGroupModal(false);
      setEditingOptionGroup(null);
      setOptionGroupFormData({
        name: '',
        type: 'single',
        required: false,
        hasPricing: false,
        options: [{ name: '', price: '0' }]
      });
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement du groupe d\'options');
    }
  };



  return (
    <div className="products-page">
      <div className="products-header">
        <div className="container">
          <div className="header-content">
            <div>
              <motion.button
                className="btn-back"
                onClick={() => navigate('/admin/dashboard')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaArrowLeft /> Retour
              </motion.button>
              <h1>Gestion des Produits</h1>
            </div>
            <motion.button
              className="btn-add"
              onClick={() => {
                setEditingProduct(null);
                setFormData({
                  name: '',
                  price: '',
                  description: '',
                  category: 'burgers',
                  image: '',
                  allowSupplements: false,
                  availableSupplements: [],
                  optionGroups: []
                });
                setShowModal(true);
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPlus /> Ajouter Produit
            </motion.button>
          </div>
        </div>
      </div>

      <div className="container products-content">
        <div className="products-grid">
          {products.length === 0 ? (
            <div className="empty-state">
              <p>Aucun produit disponible</p>
              <p style={{ fontSize: '14px', color: '#999', marginTop: '10px' }}>
                Importez les données depuis Firebase ou ajoutez votre premier produit
              </p>
            </div>
          ) : (
            products.map((product, index) => (
            <motion.div
              key={product.id}
              className="product-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <div className="product-image">
                <img src={product.image} alt={product.name} />
              </div>
              <div className="product-info">
                <h3>{product.name}</h3>
                <p className="product-category">{categories.find(c => c.id === product.category)?.name}</p>
                <p className="product-description">{product.description}</p>
                <div className="product-footer">
                  <span className="product-price">{product.price ? product.price.toFixed(2) : '0.00'}€</span>
                  <div className="product-actions">
                    <button className="btn-edit" onClick={() => handleEdit(product)}>
                      <FaEdit />
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(product.id)}>
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))
          )}
        </div>
      </div>

      <AnimatePresence>
        {showModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <h2>{editingProduct ? 'Modifier Produit' : 'Ajouter Produit'}</h2>
              
              <form onSubmit={handleSubmit} className="product-form">
                <div className="form-group">
                  <label>Nom du produit</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Prix (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <div className="category-header">
                      <label>Catégorie</label>
                      <button 
                        type="button" 
                        className="btn-add-category-inline"
                        onClick={() => setShowCategoryModal(true)}
                        title="Ajouter une nouvelle catégorie"
                      >
                        <FaPlus /> Nouvelle
                      </button>
                    </div>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      required
                    >
                      <option value="">Sélectionner une catégorie</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.icon} {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FaImage /> Image du produit
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                  />
                  {uploading && <p className="uploading-text">Téléchargement...</p>}
                  {formData.image && (
                    <div className="image-preview">
                      <img src={formData.image} alt="Preview" />
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.allowSupplements}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        allowSupplements: e.target.checked,
                        availableSupplements: e.target.checked ? formData.availableSupplements : []
                      })}
                    />
                    <span>Autoriser les suppléments pour ce produit</span>
                  </label>
                </div>

                {formData.allowSupplements && (
                  <div className="form-group">
                    <div className="supplements-header">
                      <label>Suppléments disponibles</label>
                      <button 
                        type="button" 
                        className="btn-add-supplement-inline"
                        onClick={() => {
                          setEditingSupplement(null);
                          setSupplementFormData({ name: '', price: '' });
                          setShowSupplementModal(true);
                        }}
                      >
                        <FaPlus /> Nouveau
                      </button>
                    </div>
                    {supplements.length === 0 ? (
                      <p className="no-supplements">
                        Aucun supplément disponible. Cliquez sur "Nouveau" pour créer le premier.
                      </p>
                    ) : (
                      <div className="supplements-selection">
                        {supplements.map(supplement => (
                          <div key={supplement.id} className="supplement-item-inline">
                            <label className="supplement-checkbox">
                              <input
                                type="checkbox"
                                checked={formData.availableSupplements.includes(supplement.id)}
                                onChange={() => toggleSupplement(supplement.id)}
                              />
                              <span>{supplement.name} (+{supplement.price.toFixed(2)}€)</span>
                            </label>
                            <div className="supplement-actions-inline">
                              <button 
                                type="button"
                                className="btn-edit-tiny" 
                                onClick={() => handleEditSupplement(supplement)}
                                title="Modifier"
                              >
                                <FaEdit />
                              </button>
                              <button 
                                type="button"
                                className="btn-delete-tiny" 
                                onClick={() => handleDeleteSupplement(supplement.id)}
                                title="Supprimer"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <div className="option-groups-header">
                    <label>Groupes d'options</label>
                    <button 
                      type="button" 
                      className="btn-add-option-group-inline"
                      onClick={() => {
                        setEditingOptionGroup(null);
                        setOptionGroupFormData({
                          name: '',
                          type: 'single',
                          required: false,
                          hasPricing: false,
                          options: [{ name: '', price: '0' }]
                        });
                        setShowOptionGroupModal(true);
                      }}
                    >
                      <FaPlus /> Nouveau Groupe
                    </button>
                  </div>
                  {optionGroups.length === 0 ? (
                    <p className="no-option-groups">
                      Aucun groupe d'options disponible. Créez des groupes pour permettre aux clients de personnaliser leurs commandes.
                    </p>
                  ) : (
                    <div className="option-groups-selection">
                      {optionGroups.map(group => (
                        <div key={group.id} className="option-group-item-inline">
                          <label className="option-group-checkbox">
                            <input
                              type="checkbox"
                              checked={formData.optionGroups.includes(group.id)}
                              onChange={() => toggleOptionGroup(group.id)}
                            />
                            <div className="option-group-info">
                              <span className="group-name">{group.name}</span>
                              <span className="group-details">
                                {group.type === 'single' ? 'Choix unique' : 'Choix multiple'} 
                                {group.required && ' • Obligatoire'}
                                {' • '}
                                {group.options.length} option{group.options.length > 1 ? 's' : ''}
                              </span>
                            </div>
                          </label>
                          <div className="option-group-actions-inline">
                            <button 
                              type="button"
                              className="btn-edit-tiny" 
                              onClick={() => handleEditOptionGroup(group)}
                              title="Modifier"
                            >
                              <FaEdit />
                            </button>
                            <button 
                              type="button"
                              className="btn-delete-tiny" 
                              onClick={() => handleDeleteOptionGroup(group.id)}
                              title="Supprimer"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                    Annuler
                  </button>
                  <button type="submit" className="btn-save" disabled={uploading || !formData.image}>
                    {editingProduct ? 'Modifier' : 'Ajouter'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Supplément */}
      <AnimatePresence>
        {showSupplementModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSupplementModal(false)}
          >
            <motion.div
              className="modal-content modal-supplement"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <h2>Nouveau Supplément</h2>
              <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
                Créez un nouveau supplément qui pourra être ajouté aux produits
              </p>
              
              <form onSubmit={handleSupplementSubmit} className="supplement-form">
                <div className="form-group">
                  <label>Nom du supplément</label>
                  <input
                    type="text"
                    value={supplementFormData.name}
                    onChange={(e) => setSupplementFormData({ ...supplementFormData, name: e.target.value })}
                    placeholder="Ex: Fromage, Bacon, Avocat..."
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Prix (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={supplementFormData.price}
                    onChange={(e) => setSupplementFormData({ ...supplementFormData, price: e.target.value })}
                    placeholder="Ex: 1.50"
                    required
                  />
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-cancel" onClick={() => setShowSupplementModal(false)}>
                    Annuler
                  </button>
                  <button type="submit" className="btn-save">
                    Ajouter
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Nouvelle Catégorie */}
      <AnimatePresence>
        {showCategoryModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCategoryModal(false)}
          >
            <motion.div
              className="modal-content modal-category"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <h2>Nouvelle Catégorie</h2>
              <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
                Créez une nouvelle catégorie pour organiser vos produits
              </p>
              
              <form onSubmit={handleCategorySubmit} className="category-form">
                <div className="form-group">
                  <label>Nom de la catégorie</label>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Ex: Desserts, Entrées, Plats..."
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Icône (emoji)</label>
                  <input
                    type="text"
                    value={newCategoryIcon}
                    onChange={(e) => setNewCategoryIcon(e.target.value)}
                    placeholder="Ex: 🍰, 🥙, 🍜"
                    maxLength={2}
                    required
                  />
                  <small style={{ color: '#999', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                    Utilisez un emoji pour représenter cette catégorie
                  </small>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-cancel" onClick={() => setShowCategoryModal(false)}>
                    Annuler
                  </button>
                  <button type="submit" className="btn-save">
                    Ajouter
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Groupe d'Options */}
      <AnimatePresence>
        {showOptionGroupModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowOptionGroupModal(false)}
          >
            <motion.div
              className="modal-content modal-option-group"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <h2>{editingOptionGroup ? 'Modifier Groupe d\'Options' : 'Nouveau Groupe d\'Options'}</h2>
              <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
                Créez un groupe d'options pour permettre aux clients de personnaliser leurs commandes
              </p>
              
              <form onSubmit={handleOptionGroupSubmit} className="option-group-form">
                <div className="form-group">
                  <label>Nom du groupe</label>
                  <input
                    type="text"
                    value={optionGroupFormData.name}
                    onChange={(e) => setOptionGroupFormData({ ...optionGroupFormData, name: e.target.value })}
                    placeholder="Ex: Taille, Sauce, Accompagnement..."
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Type de sélection</label>
                    <select
                      value={optionGroupFormData.type}
                      onChange={(e) => setOptionGroupFormData({ 
                        ...optionGroupFormData, 
                        type: e.target.value as 'single' | 'multiple' 
                      })}
                    >
                      <option value="single">Choix unique (radio)</option>
                      <option value="multiple">Choix multiple (checkbox)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={optionGroupFormData.required}
                        onChange={(e) => setOptionGroupFormData({ 
                          ...optionGroupFormData, 
                          required: e.target.checked 
                        })}
                      />
                      <span>Sélection obligatoire</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={optionGroupFormData.hasPricing}
                      onChange={(e) => setOptionGroupFormData({ 
                        ...optionGroupFormData, 
                        hasPricing: e.target.checked 
                      })}
                    />
                    <span>Les options ont des prix différents</span>
                  </label>
                  <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                    Décochez si toutes les options sont gratuites (ex: choix de base pour pizza)
                  </small>
                </div>

                <div className="form-group">
                  <div className="options-header">
                    <label>Options disponibles</label>
                    <button 
                      type="button" 
                      className="btn-add-option"
                      onClick={addOptionToGroup}
                    >
                      <FaPlus /> Ajouter Option
                    </button>
                  </div>
                  
                  <div className="options-list">
                    {optionGroupFormData.options.map((option, index) => (
                      <div key={index} className={`option-item ${!optionGroupFormData.hasPricing ? 'no-pricing' : ''}`}>
                        <input
                          type="text"
                          placeholder="Nom de l'option"
                          value={option.name}
                          onChange={(e) => updateOptionInGroup(index, 'name', e.target.value)}
                          required
                        />
                        {optionGroupFormData.hasPricing && (
                          <>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="Prix"
                              value={option.price}
                              onChange={(e) => updateOptionInGroup(index, 'price', e.target.value)}
                              required
                            />
                            <span className="price-suffix">€</span>
                          </>
                        )}
                        {optionGroupFormData.options.length > 1 && (
                          <button
                            type="button"
                            className="btn-remove-option"
                            onClick={() => removeOptionFromGroup(index)}
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-cancel" onClick={() => setShowOptionGroupModal(false)}>
                    Annuler
                  </button>
                  <button type="submit" className="btn-save">
                    {editingOptionGroup ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Products;
